/**
 * Letter of Medical Necessity (LMN) Explainer Page
 * With dynamic categories from Remote Config
 */

'use client';

import { useEffect, useState } from 'react';
import { getRemoteConfigValue } from '@/lib/firebase.client';

// Default categories (used when Remote Config is not available)
const DEFAULT_CATEGORIES = [
  { name: 'Massage Therapy', icon: '💆', description: 'For chronic pain or injury recovery' },
  { name: 'Acupuncture', icon: '📍', description: 'Alternative pain management' },
  { name: 'Chiropractic Care', icon: '🦴', description: 'Spinal and musculoskeletal treatment' },
  { name: 'Exercise Equipment', icon: '🏋️', description: 'Physical therapy and rehabilitation' },
  { name: 'Air Purifiers', icon: '💨', description: 'Respiratory conditions and allergies' },
  { name: 'Special Diets', icon: '🥗', description: 'Medical conditions requiring specific nutrition' },
  { name: 'Weight Loss Programs', icon: '⚖️', description: 'Obesity treatment prescribed by doctor' },
  { name: 'Service Animals', icon: '🐕', description: 'Trained animals for medical conditions' },
];

export default function LMNPage() {
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);

  useEffect(() => {
    // Fetch categories from Remote Config
    try {
      const configValue = getRemoteConfigValue('lmn_categories');
      if (configValue) {
        const parsedCategories = JSON.parse(configValue);
        if (Array.isArray(parsedCategories) && parsedCategories.length > 0) {
          setCategories(parsedCategories);
        }
      }
    } catch (error) {
      console.log('Using default LMN categories');
    }
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 py-16 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12 animate-fade-in">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary-50 rounded-full border border-primary-200 mb-6">
            <svg className="w-4 h-4 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
            </svg>
            <span className="text-sm font-semibold text-primary-600">Medical Documentation</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Letter of Medical Necessity
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Everything you need to know about obtaining and using an LMN for FSA/HSA reimbursement
          </p>
        </div>

        {/* What is an LMN */}
        <section className="mb-12 bg-white rounded-3xl shadow-lg border border-gray-100 p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">What is a Letter of Medical Necessity?</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            A Letter of Medical Necessity (LMN) is a formal document from a licensed healthcare provider that states a specific treatment, service, or product is medically necessary for the diagnosis, treatment, or prevention of a disease or medical condition.
          </p>
          <p className="text-gray-700 leading-relaxed">
            For FSA and HSA purposes, an LMN serves as official documentation that certain items or services qualify as eligible medical expenses under IRS guidelines, even if they might not typically be considered medical expenses.
          </p>
        </section>

        {/* Popular Eligible Categories - Dynamic from Remote Config */}
        <section className="mb-12">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Popular Eligible Categories</h2>
            <p className="text-gray-600">Common items and services that typically require an LMN</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {categories.map((item) => (
              <div key={item.name} className="group bg-white rounded-2xl p-6 border-2 border-gray-100 hover:border-primary-300 hover:shadow-lg transition-all duration-200">
                <div className="flex items-start gap-4">
                  <div className="text-4xl group-hover:scale-110 transition-transform duration-200">{item.icon}</div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">{item.name}</h3>
                    <p className="text-gray-600 text-sm">{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* When You Need One */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">When Do You Need an LMN?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { title: 'Dual-Purpose Items', description: 'Items that have both medical and personal use', icon: '🔄' },
              { title: 'Non-Traditional Treatments', description: 'Alternative therapies and specialized care', icon: '🌿' },
              { title: 'Specialized Equipment', description: 'Medical devices and adaptive equipment', icon: '🏥' },
              { title: 'Cosmetic Procedures', description: 'Medically necessary cosmetic treatments', icon: '💉' },
            ].map((item) => (
              <div key={item.title} className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-2xl p-6 border border-primary-200">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">{item.icon}</span>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">{item.title}</h3>
                    <p className="text-gray-700 text-sm">{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* What Should Be Included */}
        <section className="mb-12 bg-white rounded-3xl shadow-lg border border-gray-100 p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">What Should an LMN Include?</h2>
          <p className="text-gray-700 mb-6">A proper Letter of Medical Necessity should contain:</p>
          <ul className="space-y-4">
            {[
              { title: 'Patient Information', desc: 'Your full name, date of birth, and contact information' },
              { title: 'Healthcare Provider Details', desc: 'Provider\'s name, credentials, contact information, and license number' },
              { title: 'Medical Diagnosis', desc: 'Specific diagnosis or medical condition (with ICD-10 code if applicable)' },
              { title: 'Treatment Justification', desc: 'Explanation of why the item/service is medically necessary' },
              { title: 'Item/Service Description', desc: 'Clear description of the specific item, service, or treatment' },
              { title: 'Duration', desc: 'How long the treatment or use of the item is expected to be necessary' },
              { title: 'Provider Signature', desc: 'Original signature and date from the healthcare provider' },
            ].map((item, index) => (
              <li key={index} className="flex items-start space-x-4">
                <span className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                  {index + 1}
                </span>
                <div>
                  <h4 className="font-bold text-gray-900">{item.title}</h4>
                  <p className="text-gray-600 text-sm">{item.desc}</p>
                </div>
              </li>
            ))}
          </ul>
        </section>

        {/* How to Get One */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">How to Get an LMN</h2>
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-3xl shadow-lg p-8 border border-blue-200">
            <div className="space-y-6">
              {[
                { title: 'Schedule an Appointment', desc: 'Contact your healthcare provider and explain that you need documentation for FSA/HSA reimbursement.' },
                { title: 'Discuss Your Needs', desc: 'Explain the specific item or service you need and why you believe it\'s medically necessary for your condition.' },
                { title: 'Request the Letter', desc: 'If your provider agrees it\'s medically necessary, request a formal letter on their letterhead with all required information.' },
                { title: 'Review and Submit', desc: 'Review the letter for completeness and submit it with your FSA/HSA reimbursement claim along with receipts.' },
              ].map((step, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg shadow-md">
                    {index + 1}
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-lg mb-1">{step.title}</h4>
                    <p className="text-gray-700">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Example Scenarios */}
        <section className="mb-12 bg-white rounded-3xl shadow-lg border border-gray-100 p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Real-World Examples</h2>
          <div className="space-y-6">
            <div className="border-l-4 border-green-500 pl-6 py-2">
              <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                <span className="text-green-500">✓</span> Massage Therapy
              </h4>
              <p className="text-gray-700 text-sm">
                You suffer from chronic back pain. Your doctor prescribes massage therapy as part of your treatment plan and provides an LMN stating the medical necessity. With the LMN, massage therapy becomes FSA/HSA eligible.
              </p>
            </div>
            <div className="border-l-4 border-green-500 pl-6 py-2">
              <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                <span className="text-green-500">✓</span> Special Diet
              </h4>
              <p className="text-gray-700 text-sm">
                You have celiac disease and need gluten-free foods. With an LMN from your doctor explaining the medical necessity, the incremental cost difference between gluten-free and regular foods may be eligible.
              </p>
            </div>
            <div className="border-l-4 border-green-500 pl-6 py-2">
              <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                <span className="text-green-500">✓</span> Exercise Equipment
              </h4>
              <p className="text-gray-700 text-sm">
                Your physical therapist prescribes specific exercise equipment for rehabilitation after an injury. An LMN documenting the medical necessity makes this equipment FSA/HSA eligible.
              </p>
            </div>
          </div>
        </section>

        {/* Important Notes */}
        <div className="bg-gradient-to-br from-amber-50 to-amber-100 border-2 border-amber-200 rounded-3xl p-8">
          <h3 className="font-bold text-amber-900 text-xl mb-4 flex items-center gap-2">
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            Important Reminders
          </h3>
          <ul className="space-y-2 text-amber-900">
            <li className="flex items-start gap-2">
              <span className="text-amber-600 mt-1">•</span>
              <span>An LMN does not guarantee reimbursement - your plan administrator has final say</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-amber-600 mt-1">•</span>
              <span>Keep copies of all LMNs with your tax records for at least 7 years</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-amber-600 mt-1">•</span>
              <span>Some providers may charge a fee for writing an LMN</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-amber-600 mt-1">•</span>
              <span>Always check with your specific plan administrator about their LMN requirements</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-amber-600 mt-1">•</span>
              <span>An LMN is different from a prescription - both may be required for certain items</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
